import dayjs, { type Dayjs } from 'dayjs';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { getReservationReports } from '../api/endpoints/reports';
import type { ReservationReportRow } from '../api/types/reports';
import type { BookingStatus, ProviderType } from '../api/types/common';
import { ApiErrorAlert } from './ApiErrorAlert';
import { ReservationIdentifierLink } from './ReservationIdentifierLink';

const filterSchema = z
  .object({
    startDate: z.custom<Dayjs>((v) => dayjs.isDayjs(v) && v.isValid(), { message: 'Başlangıç tarihi seçin.' }),
    endDate: z.custom<Dayjs>((v) => dayjs.isDayjs(v) && v.isValid(), { message: 'Bitiş tarihi seçin.' }),
  })
  .refine((d) => !d.endDate.isBefore(d.startDate, 'day'), {
    message: 'Bitiş tarihi başlangıçtan önce olamaz.',
    path: ['endDate'],
  });

type FilterValues = z.infer<typeof filterSchema>;

function pick(row: ReservationReportRow, keys: string[]): string {
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined && value !== null && value !== '') return String(value);
  }
  return '—';
}

/**
 * Generic date-range reservation report list over the shared `Report/Reservations`
 * endpoint, fixed to one `providerType` + `status`. Plain MUI Table (no
 * @mui/x-data-grid — pinned TS 4.9.5). Used by the Air "İptal Rezervasyonlar Listesi"
 * route (`air/reservations/cancelled`); hotel/bus/transfer/rentCar don't expose a
 * cancelled-list menu item. The "Rezervasyon / Bilet Listesi" pages predate it and keep
 * their own copy.
 */
export function ReservationReportList({
  providerType,
  status,
  title,
  detailBasePath,
  defaultRangeDays = 90,
}: {
  providerType: ProviderType;
  status: BookingStatus;
  title: string;
  detailBasePath: string;
  defaultRangeDays?: number;
}) {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FilterValues>({
    resolver: zodResolver(filterSchema),
    defaultValues: { startDate: dayjs().subtract(defaultRangeDays, 'day'), endDate: dayjs() },
  });

  const search = useMutation({
    mutationFn: (values: FilterValues) =>
      getReservationReports({
        insertStartDate: values.startDate.format('YYYY-MM-DD'),
        insertEndDate: values.endDate.format('YYYY-MM-DD'),
        status,
        providerType,
      }),
  });

  const rows = search.data?.reservationReports ?? [];
  const hasSearched = search.isSuccess || search.isError;
  const onSubmit = handleSubmit((values) => search.mutate(values));

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {title}
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box component="form" onSubmit={onSubmit}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: 'flex-start' }}>
            <Controller
              name="startDate"
              control={control}
              render={({ field }) => (
                <DatePicker
                  label="Başlangıç tarihi"
                  value={field.value}
                  onChange={field.onChange}
                  slotProps={{
                    textField: { size: 'small', error: !!errors.startDate, helperText: errors.startDate?.message },
                  }}
                />
              )}
            />
            <Controller
              name="endDate"
              control={control}
              render={({ field }) => (
                <DatePicker
                  label="Bitiş tarihi"
                  value={field.value}
                  onChange={field.onChange}
                  slotProps={{
                    textField: { size: 'small', error: !!errors.endDate, helperText: errors.endDate?.message },
                  }}
                />
              )}
            />
            <Button type="submit" variant="contained" disabled={search.isPending} sx={{ mt: { sm: 0.5 } }}>
              {search.isPending ? 'Aranıyor…' : 'Ara'}
            </Button>
          </Stack>
        </Box>
        <ApiErrorAlert error={search.error} sx={{ mt: 2 }} />
      </Paper>

      {search.isPending && <CircularProgress size={24} sx={{ mb: 2 }} />}

      {hasSearched && !search.isError && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>PNR / Bilet</TableCell>
                <TableCell>Durum</TableCell>
                <TableCell>Sağlayıcı</TableCell>
                <TableCell>Yolcu</TableCell>
                <TableCell>Güzergah</TableCell>
                <TableCell>Toplam</TableCell>
                <TableCell>Tarih</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      Bu kriterlere uygun kayıt bulunamadı.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {rows.map((row, index) => (
                <TableRow key={pick(row, ['shoppingCartId']) + index} hover>
                  <TableCell>
                    <ReservationIdentifierLink row={row} detailBasePath={detailBasePath} />
                  </TableCell>
                  <TableCell>{pick(row, ['statu', 'status'])}</TableCell>
                  <TableCell>{pick(row, ['provider'])}</TableCell>
                  <TableCell>{[row.firstName, row.lastName].filter(Boolean).join(' ') || '—'}</TableCell>
                  <TableCell>{[row.origin, row.destination].filter(Boolean).join(' → ') || '—'}</TableCell>
                  <TableCell>{pick(row, ['totalFare'])}</TableCell>
                  <TableCell>{pick(row, ['bookedAt', 'prebookedAt', 'insertedAt'])}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
